/**
 * Tap Behavior Detection Strategy (Mobile)
 * Monitors touch interactions on specific elements
 * Analyzes tap duration, precision, movement, and timing patterns
 */
import { BaseStrategy } from '../strategy.js';
import { analyzeIntervals, scoreCoefficientOfVariation, gaussian } from '../math-utils.js';
export class TapStrategy extends BaseStrategy {
    constructor(options) {
        super();
        this.name = 'tap';
        this.defaultWeight = 0.35;
        this.events = [];
        this.targetSelectors = ['button', 'a', 'input[type="submit"]', '[role="button"]'];
        this.touchStartListeners = new Map();
        this.touchEndListeners = new Map();
        this.activeTouches = new Map();
        this.isActive = false;
        if (options === null || options === void 0 ? void 0 : options.targetSelectors) {
            this.targetSelectors = options.targetSelectors;
        }
    }
    start() {
        if (this.isActive)
            return;
        this.isActive = true;
        this.attachTouchListeners();
    }
    addTarget(selector) {
        if (!this.targetSelectors.includes(selector)) {
            this.targetSelectors.push(selector);
            if (this.isActive) {
                this.attachTouchListenersForSelector(selector);
            }
        }
    }
    attachTouchListeners() {
        this.targetSelectors.forEach(selector => {
            this.attachTouchListenersForSelector(selector);
        });
    }
    attachTouchListenersForSelector(selector) {
        const elements = document.querySelectorAll(selector);
        elements.forEach(element => {
            if (this.touchStartListeners.has(element))
                return;
            const startListener = (e) => {
                const touchEvent = e;
                // Only track first touch (ignore multi-touch)
                if (touchEvent.touches.length !== 1)
                    return;
                const touch = touchEvent.touches[0];
                this.activeTouches.set(touch.identifier, {
                    x: touch.clientX,
                    y: touch.clientY,
                    timestamp: Date.now(),
                    element,
                });
            };
            const endListener = (e) => {
                const touchEvent = e;
                if (touchEvent.changedTouches.length !== 1)
                    return;
                const touch = touchEvent.changedTouches[0];
                const startData = this.activeTouches.get(touch.identifier);
                if (!startData || startData.element !== element)
                    return;
                const now = Date.now();
                const duration = now - startData.timestamp;
                const dx = touch.clientX - startData.x;
                const dy = touch.clientY - startData.y;
                const movement = Math.sqrt(dx * dx + dy * dy);
                const rect = element.getBoundingClientRect();
                const inViewport = (rect.top >= 0 &&
                    rect.left >= 0 &&
                    rect.bottom <= window.innerHeight &&
                    rect.right <= window.innerWidth);
                // Analyze tap position
                const tx = touch.clientX;
                const ty = touch.clientY;
                const overElement = (tx >= rect.left &&
                    tx <= rect.right &&
                    ty >= rect.top &&
                    ty <= rect.bottom);
                let position;
                if (!overElement) {
                    position = 'outside';
                }
                else {
                    const centerX = rect.left + rect.width / 2;
                    const centerY = rect.top + rect.height / 2;
                    const distanceFromCenter = Math.sqrt((tx - centerX) ** 2 + (ty - centerY) ** 2);
                    // Dead center = within 2px
                    position = distanceFromCenter < 2 ? 'dead-center' : 'over-element';
                }
                this.events.push({
                    x: tx,
                    y: ty,
                    rect,
                    inViewport,
                    position,
                    duration,
                    movement,
                    timestamp: now,
                });
                // Notify detector - taps are high-value events
                this.notifyEvent(1.0);
                this.activeTouches.delete(touch.identifier);
            };
            element.addEventListener('touchstart', startListener, { passive: true });
            element.addEventListener('touchend', endListener, { passive: true });
            this.touchStartListeners.set(element, startListener);
            this.touchEndListeners.set(element, endListener);
        });
    }
    stop() {
        if (!this.isActive)
            return;
        this.isActive = false;
        this.touchStartListeners.forEach((listener, element) => {
            element.removeEventListener('touchstart', listener);
        });
        this.touchStartListeners.clear();
        this.touchEndListeners.forEach((listener, element) => {
            element.removeEventListener('touchend', listener);
        });
        this.touchEndListeners.clear();
        this.activeTouches.clear();
    }
    reset() {
        this.events = [];
        this.activeTouches.clear();
    }
    score() {
        if (this.events.length === 0)
            return undefined;
        let score = 0;
        let factors = 0;
        // 1. Position accuracy
        let positionScore = 0;
        for (const tap of this.events) {
            switch (tap.position) {
                case 'outside':
                    positionScore += 0.0; // Bot
                    break;
                case 'dead-center':
                    positionScore += 0.5; // Suspicious
                    break;
                case 'over-element':
                    positionScore += 1.0; // Human
                    break;
            }
        }
        score += positionScore / this.events.length;
        factors++;
        // 2. Tap duration variation - Humans have natural variation (50-150ms typical)
        const durations = this.events.map(e => e.duration);
        if (durations.length >= 2) {
            const durationAnalysis = analyzeIntervals(durations);
            if (durationAnalysis) {
                const { statistics, allIdentical } = durationAnalysis;
                // All identical = bot
                if (allIdentical) {
                    return 0.05;
                }
                // Human tap durations have moderate CV (~0.3-0.5)
                score += scoreCoefficientOfVariation(statistics.cv);
                factors++;
                // Ideal duration: 70-120ms
                score += gaussian(statistics.mean, 95, 40);
                factors++;
            }
        }
        // 3. Tap movement - Real fingers move slightly (1-5px), bots are often 0px
        const movements = this.events.map(e => e.movement);
        if (movements.length > 0) {
            const avgMovement = movements.reduce((a, b) => a + b, 0) / movements.length;
            // Some movement is natural (1-5px), too much is a swipe, zero is suspicious
            score += gaussian(avgMovement, 2, 3);
            factors++;
        }
        // 4. Tap interval timing - Time between taps should vary naturally
        if (this.events.length >= 3) {
            const intervals = [];
            for (let i = 1; i < this.events.length; i++) {
                intervals.push(this.events[i].timestamp - this.events[i - 1].timestamp);
            }
            const intervalAnalysis = analyzeIntervals(intervals);
            if (intervalAnalysis && !intervalAnalysis.allIdentical) {
                score += scoreCoefficientOfVariation(intervalAnalysis.statistics.cv);
                factors++;
            }
        }
        return factors > 0 ? score / factors : undefined;
    }
    getDebugInfo() {
        return {
            eventCount: this.events.length,
            positions: {
                outside: this.events.filter(e => e.position === 'outside').length,
                deadCenter: this.events.filter(e => e.position === 'dead-center').length,
                overElement: this.events.filter(e => e.position === 'over-element').length,
            },
            durations: this.events.map(e => e.duration),
            movements: this.events.map(e => e.movement),
            inViewport: this.events.filter(e => e.inViewport).length,
            trackedElements: this.touchStartListeners.size,
        };
    }
}
